import React, { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { Stars, Sphere, Ring } from '@react-three/drei'

const BlackHoleScene = ({ 
  gravityEnabled, 
  onFPSUpdate, 
  blackHoleMass = 4.3e6, 
  particleCount = 1000, 
  diskDensity = 1000, 
  blackHoleSpin = 0.9,
  diskBrightness = 1.0,
  lensingStrength = 1.5
}) => {
  const blackHoleRef = useRef()
  const diskRef = useRef()

  console.log('BlackHoleScene rendering with params:', { blackHoleMass, diskBrightness, blackHoleSpin })

  // Calculate black hole size based on mass
  const blackHoleSize = Math.max(2, Math.min(6, 3 + Math.log10(blackHoleMass / 1e6) * 0.5))
  const diskInnerRadius = blackHoleSize * 1.3
  const diskOuterRadius = blackHoleSize * 5

  useFrame((state, delta) => {
    if (blackHoleRef.current) {
      blackHoleRef.current.rotation.y += delta * 0.1 * blackHoleSpin
    }
    if (diskRef.current) {
      diskRef.current.rotation.y += delta * 0.5
    }
  })

  return (
    <>
      {/* Beautiful Starfield Background */}
      <Stars
        radius={300}
        depth={60}
        count={8000}
        factor={7}
        saturation={0}
        fade={true}
        speed={1}
      />
      
      {/* Enhanced Lighting System */}
      <ambientLight intensity={0.05} color="#001122" />
      <pointLight position={[0, 0, 0]} intensity={3} color="#ffffff" />
      <pointLight position={[0, 0, 0]} intensity={2} color="#ff6600" />
      <directionalLight position={[10, 10, 5]} intensity={0.8} color="#4080ff" />
      <spotLight
        position={[0, 10, 0]}
        angle={0.3}
        penumbra={1}
        intensity={1.5}
        color="#ff6600"
      />
      {/* Black Hole */}
      <Sphere ref={blackHoleRef} args={[blackHoleSize, 128, 128]}>
        <meshStandardMaterial 
          color="#000000" 
          emissive="#220022"
          emissiveIntensity={0.2 * blackHoleSpin}
          roughness={0.9}
          metalness={0.1}
          normalScale={[0.5, 0.5]}
        />
      </Sphere>

      {/* Main Accretion Disk */}
      <Ring ref={diskRef} args={[diskInnerRadius, diskOuterRadius, 256]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial 
          color="#ff6600" 
          emissive="#ff3300"
          emissiveIntensity={0.8 * diskBrightness}
          transparent 
          opacity={0.9 * diskBrightness}
          side={2}
          roughness={0.05}
          metalness={0.95}
          envMapIntensity={1.0}
        />
      </Ring>

      {/* Outer Accretion Disk Layer */}
      <Ring args={[diskOuterRadius * 1.2, diskOuterRadius * 1.8, 128]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial 
          color="#ff3300" 
          emissive="#ff1100"
          emissiveIntensity={0.4 * diskBrightness}
          transparent 
          opacity={0.6 * diskBrightness}
          side={2}
          roughness={0.1}
          metalness={0.8}
        />
      </Ring>

      {/* Inner Photon Ring */}
      <Ring args={[blackHoleSize * 1.1, blackHoleSize * 1.3, 128]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial 
          color="#ffffff" 
          emissive="#ffffff"
          emissiveIntensity={2.0 * diskBrightness}
          transparent 
          opacity={0.8 * diskBrightness}
          side={2}
          roughness={0.0}
          metalness={1.0}
          envMapIntensity={2.0}
        />
      </Ring>

      {/* Dynamic Particle System */}
      <points>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={Math.min(particleCount, 500)}
            array={new Float32Array(Array.from({length: Math.min(particleCount, 500) * 3}, () => (Math.random() - 0.5) * 25))}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial 
          size={0.1}
          color="#00ffff"
          transparent
          opacity={0.8}
          sizeAttenuation={true}
        />
      </points>
    </>
  )
}

export default BlackHoleScene
